import { useState, useEffect } from 'react';
import { MatchData } from '@/interfaces/liveGamesType';
import { DashboardState, TabType, GroupedMatches } from '../types';
import { fetchLiveMatches, fetchHistoricalMatches, fetchHistoricalMatchesFree } from '../services/api';
import { getMainLeagues, filterMatches, getMatchesByTab, groupMatchesByLeague, isDateInPast } from '../utils';
import { useAuth } from '@/context/AuthContext';
import { useTheme, useMediaQuery } from '@mui/material';
export const useMatches = () => {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // Estado do componente
  const [state, setState] = useState<DashboardState>({
    date: new Date(),
    loading: true,
    tabValue: TabType.ALL,
    leagues: [],
    liveMatches: [],
    finishedMatches: [],
    selectedMatch: null,
    favorites: [],
    searchQuery: '',
    openDetailsMobile: false
  });

  // Extraindo valores do estado para facilitar o acesso
  const {
    date,
    loading,
    tabValue,
    leagues,
    liveMatches,
    finishedMatches,
    selectedMatch,
    favorites,
    searchQuery,
    openDetailsMobile
  } = state;

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, [date, isAuthenticated]);

  // Função para carregar todos os dados
  const loadData = async () => {
    // Atualizar estado para loading
    setState(prev => ({ ...prev, loading: true }));

    try {
      // Carregar ligas principais
      const mainLeagues = getMainLeagues();

      // Carregar partidas ao vivo (apenas se a data for hoje)
      const isToday = new Date().toDateString() === date.toDateString();
      let liveData: MatchData[] = [];

      if (isToday) {
        liveData = await fetchLiveMatches(isAuthenticated);
      }

      // Carregar partidas históricas
      // Se a data for no passado, usar getHistMatches
      let histData: MatchData[] = [];

      if (isDateInPast(date)) {
        // Para datas passadas, usar o endpoint de partidas históricas
        histData = await fetchHistoricalMatches(isAuthenticated, date);

        // Se não retornar dados, tentar com a versão gratuita
        if (!histData || histData.length === 0) {
          histData = await fetchHistoricalMatchesFree(isAuthenticated, date);
        }
      } else if (!isToday) {
        // Para datas futuras, usar o endpoint de previsões
        // Implementar lógica para previsões futuras se necessário
      }

      // Definir match selecionado se não houver nenhum ainda
      let newSelectedMatch = selectedMatch;
      if (!selectedMatch && liveData.length > 0) {
        newSelectedMatch = liveData[0];
      } else if (!selectedMatch && histData.length > 0) {
        newSelectedMatch = histData[0];
      }

      // Atualizar estado
      setState(prev => ({
        ...prev,
        leagues: mainLeagues,
        liveMatches: liveData,
        finishedMatches: histData,
        selectedMatch: newSelectedMatch,
        loading: false
      }));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Handler para mudança de tabs
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setState(prev => ({ ...prev, tabValue: newValue }));
  };

  // Handler para mudar data
  const changeDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(date);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setState(prev => ({ ...prev, date: newDate }));
  };

  // Nova função para selecionar data específica
  const selectSpecificDate = (newDate: Date) => {
    setState(prev => ({ ...prev, date: newDate }));
  };

  // Handler para pesquisa
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchQuery: event.target.value }));
  };

  // Handler para favoritar/desfavoritar
  const toggleFavorite = (id: string) => {
    setState(prev => {
      if (prev.favorites.includes(id)) {
        return { ...prev, favorites: prev.favorites.filter(fav => fav !== id) };
      } else {
        return { ...prev, favorites: [...prev.favorites, id] };
      }
    });
  };

  // Handler para selecionar uma partida
  const selectMatch = (match: MatchData) => {
    setState(prev => ({ ...prev, selectedMatch: match, openDetailsMobile: isMobile }));
  };

  // Função para fechar o painel de detalhes no mobile
  const closeMobileDetails = () => {
    setState(prev => ({ ...prev, openDetailsMobile: false }));
  };

  // Obter partidas filtradas e agrupadas
  const getFilteredMatches = () => {
    // Obter partidas de acordo com a aba selecionada
    let matches = getMatchesByTab(tabValue, liveMatches, finishedMatches, favorites);

    // Aplicar filtro de pesquisa
    matches = filterMatches(matches, searchQuery);

    return matches;
  };

  // Obter partidas agrupadas por liga
  const getGroupedMatches = (): GroupedMatches => {
    const matches = getFilteredMatches();
    return groupMatchesByLeague(matches);
  };

  return {
    state,
    filteredMatches: getFilteredMatches(),
    groupedMatches: getGroupedMatches(),
    handleTabChange,
    changeDate,
    selectSpecificDate,
    handleSearch,
    toggleFavorite,
    selectMatch,
    loadData,
    isMobile,
    openDetailsMobile,
    closeMobileDetails
  };
};